import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Check, Wine, X, ArrowRightLeft, PlusCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SortableHeader, sortData } from "../components/SortableHeader";
import { useLand } from "../hooks/useLand";
import { useStandort } from "../hooks/useStandort";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KATEGORIEN = [
  "Alkoholfrei",
  "Bier",
  "Wein",
  "Spirituosen",
  "Cocktails",
  "Heißgetränke",
  "Säfte"
];

const EINHEITEN = [
  "0.2l",
  "0.3l",
  "0.4l",
  "0.5l",
  "1l",
  "Flasche",
  "Glas",
  "Tasse",
  "Eigene"
];

// Einheiten-Umrechnung
const UNIT_CONVERSIONS = {
  kg: { to: 'g', factor: 1000, label: 'kg → g' },
  g: { to: 'kg', factor: 0.001, label: 'g → kg' },
  l: { to: 'ml', factor: 1000, label: 'l → ml' },
  ml: { to: 'l', factor: 0.001, label: 'ml → l' },
};

const convertUnit = (value, fromUnit) => {
  const conversion = UNIT_CONVERSIONS[fromUnit];
  if (!conversion) return null;
  return {
    value: value * conversion.factor,
    unit: conversion.to
  };
};

export const Getraenke = () => {
  const { land, vat } = useLand();
  const { activeStandortId } = useStandort();
  const [getraenke, setGetraenke] = useState([]);
  const [zutaten, setZutaten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGetraenk, setEditingGetraenk] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    kategorie: "Alkoholfrei",
    einkaufspreis: "",
    verkaufspreis: "",
    einheit: "0.3l",
    lieferant: "",
    zutaten: []
  });
  const [selectedZutat, setSelectedZutat] = useState("");
  const [zutatMenge, setZutatMenge] = useState("");
  const [zutatEinheit, setZutatEinheit] = useState("");
  const [customEinheit, setCustomEinheit] = useState("");
  const [showCustomEinheit, setShowCustomEinheit] = useState(false);

  // Neue Zutat Dialog
  const [neueZutatDialogOpen, setNeueZutatDialogOpen] = useState(false);
  const [neueZutatForm, setNeueZutatForm] = useState({
    name: "",
    einheit: "kg",
    preis_pro_einheit: "",
    lieferant: ""
  });
  const ZUTAT_EINHEITEN = ["kg", "g", "l", "ml", "Stück", "Bund", "Packung"];
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStandortId]);

  const fetchData = async () => {
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      const [getraenkeRes, zutatenRes] = await Promise.all([
        axios.get(`${API}/getraenke`, { params, withCredentials: true }),
        axios.get(`${API}/zutaten`, { withCredentials: true })
      ]);
      setGetraenke(getraenkeRes.data);
      setZutaten(zutatenRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      kategorie: "Alkoholfrei",
      einkaufspreis: "",
      verkaufspreis: "",
      einheit: "0.3l",
      lieferant: "",
      zutaten: []
    });
    setEditingGetraenk(null);
    setSelectedZutat("");
    setZutatMenge("");
    setZutatEinheit("");
    setCustomEinheit("");
    setShowCustomEinheit(false);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (getraenk) => {
    setEditingGetraenk(getraenk);
    const isCustom = !EINHEITEN.slice(0, -1).includes(getraenk.einheit);
    setFormData({
      name: getraenk.name,
      kategorie: getraenk.kategorie,
      einkaufspreis: getraenk.einkaufspreis.toString(),
      verkaufspreis: getraenk.verkaufspreis.toString(),
      einheit: isCustom ? "Eigene" : getraenk.einheit,
      lieferant: getraenk.lieferant || "",
      zutaten: getraenk.zutaten || []
    });
    if (isCustom) {
      setCustomEinheit(getraenk.einheit);
      setShowCustomEinheit(true);
    }
    setDialogOpen(true);
  };

  // Wenn Zutat gewählt wird, setze die Standard-Einheit
  const handleZutatSelect = (zutatId) => {
    if (zutatId === "__new__") {
      setNeueZutatDialogOpen(true);
      return;
    }
    setSelectedZutat(zutatId);
    const zutat = zutaten.find(z => z.id === zutatId);
    if (zutat) {
      setZutatEinheit(zutat.einheit);
    }
  };

  // Neue Zutat erstellen
  const handleNeueZutatSubmit = async (e) => {
    e.preventDefault();
    if (!neueZutatForm.name || !neueZutatForm.preis_pro_einheit) {
      toast.error("Bitte Name und Preis angeben");
      return;
    }

    try {
      const response = await axios.post(`${API}/zutaten`, {
        name: neueZutatForm.name,
        einheit: neueZutatForm.einheit,
        preis_pro_einheit: parseFloat(neueZutatForm.preis_pro_einheit),
        lieferant: neueZutatForm.lieferant || null
      }, { withCredentials: true });

      toast.success("Zutat erstellt");
      setZutaten([...zutaten, response.data]);
      setNeueZutatDialogOpen(false);
      setNeueZutatForm({ name: "", einheit: "kg", preis_pro_einheit: "", lieferant: "" });
      
      // Automatisch die neue Zutat auswählen
      setSelectedZutat(response.data.id);
      setZutatEinheit(response.data.einheit);
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  // Einheit umrechnen
  const handleUnitConversion = () => {
    if (!zutatMenge || !zutatEinheit) return;
    
    const converted = convertUnit(parseFloat(zutatMenge), zutatEinheit);
    if (converted) {
      setZutatMenge(converted.value.toString());
      setZutatEinheit(converted.unit);
      toast.success(`Umgerechnet: ${converted.value} ${converted.unit}`);
    } else {
      toast.error("Keine Umrechnung verfügbar für diese Einheit");
    }
  };

  // Berechne Kosten basierend auf der gewählten Einheit
  const calculateZutatKosten = (zutat, menge, einheit) => {
    // Originale Einheit der Zutat
    const originalEinheit = zutat.einheit;
    let adjustedMenge = menge;
    
    // Umrechnung wenn nötig
    if (einheit !== originalEinheit) {
      // g zu kg oder kg zu g
      if ((originalEinheit === 'kg' && einheit === 'g') || 
          (originalEinheit === 'g' && einheit === 'kg')) {
        adjustedMenge = originalEinheit === 'kg' ? menge / 1000 : menge * 1000;
      }
      // ml zu l oder l zu ml
      if ((originalEinheit === 'l' && einheit === 'ml') || 
          (originalEinheit === 'ml' && einheit === 'l')) {
        adjustedMenge = originalEinheit === 'l' ? menge / 1000 : menge * 1000;
      }
    }
    
    return adjustedMenge * zutat.preis_pro_einheit;
  };

  const addZutatToGetraenk = () => {
    if (!selectedZutat || !zutatMenge) {
      toast.error("Bitte Zutat und Menge angeben");
      return;
    }

    const zutat = zutaten.find(z => z.id === selectedZutat);
    if (!zutat) return;

    const menge = parseFloat(zutatMenge);
    const kosten = calculateZutatKosten(zutat, menge, zutatEinheit);

    const newZutat = {
      zutat_id: zutat.id,
      zutat_name: zutat.name,
      menge: menge,
      einheit: zutatEinheit,
      kosten: Math.round(kosten * 100) / 100
    };

    setFormData({
      ...formData,
      zutaten: [...formData.zutaten, newZutat]
    });

    setSelectedZutat("");
    setZutatMenge("");
    setZutatEinheit("");
  };

  const removeZutatFromGetraenk = (index) => {
    setFormData({
      ...formData,
      zutaten: formData.zutaten.filter((_, i) => i !== index)
    });
  };

  const calculateZutatenKosten = () => {
    return formData.zutaten.reduce((sum, z) => sum + z.kosten, 0);
  };

  const calculateGesamtEinkauf = () => {
    const einkauf = parseFloat(formData.einkaufspreis) || 0;
    return einkauf + calculateZutatenKosten();
  };

  // MwSt-Berechnung fuer Getraenke (dynamisch nach Land: DE 19% / AT 20%)
  const MWST_GETRAENKE = vat.getraenke;

  const calculateMarge = () => {
    const verkauf = parseFloat(formData.verkaufspreis) || 0;
    const gesamt = calculateGesamtEinkauf();
    return verkauf - gesamt;
  };

  const calculateNettoPreis = () => {
    const brutto = parseFloat(formData.verkaufspreis) || 0;
    return brutto / (1 + MWST_GETRAENKE / 100);
  };

  const calculateMwStBetrag = () => {
    const brutto = parseFloat(formData.verkaufspreis) || 0;
    const netto = calculateNettoPreis();
    return brutto - netto;
  };

  const calculateMargeNetto = () => {
    const netto = calculateNettoPreis();
    const einsatz = calculateGesamtEinkauf();
    return netto - einsatz;
  };

  const calculateMargePercent = () => {
    const netto = calculateNettoPreis();
    if (netto <= 0) return "0.0";
    const einsatz = calculateGesamtEinkauf();
    return ((netto - einsatz) / netto * 100).toFixed(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.einkaufspreis || !formData.verkaufspreis) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }

    // Eigene Einheit verwenden falls gewählt
    const einheit = formData.einheit === "Eigene" ? customEinheit : formData.einheit;
    
    if (formData.einheit === "Eigene" && !customEinheit) {
      toast.error("Bitte eigene Einheit eingeben");
      return;
    }

    try {
      const payload = {
        ...formData,
        einheit: einheit,
        einkaufspreis: parseFloat(formData.einkaufspreis),
        verkaufspreis: parseFloat(formData.verkaufspreis),
        standort_id: formData.standort_id !== undefined
          ? formData.standort_id
          : (editingGetraenk ? editingGetraenk.standort_id : activeStandortId) || null,
      };

      if (editingGetraenk) {
        await axios.put(`${API}/getraenke/${editingGetraenk.id}`, payload, { withCredentials: true });
        toast.success("Getränk aktualisiert");
      } else {
        await axios.post(`${API}/getraenke`, payload, { withCredentials: true });
        toast.success("Getränk erstellt");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Getränk wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/getraenke/${id}`, { withCredentials: true });
      toast.success("Getränk gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#858585]">Laden...</div>
      </div>
    );
  }

  return (
    <div data-testid="getraenke-page">
      {/* Header */}
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Getränke</h1>
            <p className="page-subtitle">Verwalten Sie Ihre Getränkekarte inkl. Cocktails</p>
          </div>
          <Button 
            onClick={openCreateDialog}
            className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm uppercase text-xs tracking-wide btn-press"
            data-testid="add-getraenk-btn"
          >
            <Plus size={16} className="mr-2" />
            Neues Getränk
          </Button>
        </div>
      </header>

      {/* Content */}
      {getraenke.length === 0 ? (
        <div className="empty-state">
          <Wine size={64} className="mx-auto text-[#D1CFC9]" />
          <h3>Keine Getränke vorhanden</h3>
          <p>Erstellen Sie Ihr erstes Getränk mit Preiskalkulation</p>
          <Button 
            onClick={openCreateDialog}
            className="mt-4 bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm"
            data-testid="add-getraenk-empty-btn"
          >
            <Plus size={16} className="mr-2" />
            Erstes Getränk erstellen
          </Button>
        </div>
      ) : (
        <div className="data-table-container" data-testid="getraenke-table">
          <table className="data-table">
            <thead>
              <tr>
                <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Kategorie" sortKey="kategorie" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Einheit" sortKey="einheit" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Basis-EK" sortKey="einkaufspreis" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Zutaten" sortKey="zutaten_kosten" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Gesamt-EK" sortKey="gesamt_einkauf" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="VK" sortKey="verkaufspreis" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Marge %" sortKey="marge_prozent" sortConfig={sortConfig} onSort={setSortConfig} />
                <th className="text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {sortData(getraenke, sortConfig).map((getraenk) => (
                <tr key={getraenk.id} data-testid={`getraenk-row-${getraenk.id}`}>
                  <td className="font-body font-medium">{getraenk.name}</td>
                  <td>
                    <span className="badge badge-neutral">{getraenk.kategorie}</span>
                  </td>
                  <td>{getraenk.einheit}</td>
                  <td>{formatCurrency(getraenk.einkaufspreis)}</td>
                  <td className="text-[#858585]">
                    {getraenk.zutaten_kosten > 0 ? formatCurrency(getraenk.zutaten_kosten) : '-'}
                  </td>
                  <td>{formatCurrency(getraenk.gesamt_einkauf || getraenk.einkaufspreis)}</td>
                  <td>{formatCurrency(getraenk.verkaufspreis)}</td>
                  <td>
                    <span className={`badge ${getraenk.marge_prozent >= 60 ? 'badge-profit' : getraenk.marge_prozent >= 40 ? 'badge-neutral' : 'badge-loss'}`}>
                      {getraenk.marge_prozent}%
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        className="btn-icon"
                        onClick={() => openEditDialog(getraenk)}
                        data-testid={`edit-getraenk-${getraenk.id}`}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        className="btn-icon danger"
                        onClick={() => handleDelete(getraenk.id)}
                        data-testid={`delete-getraenk-${getraenk.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingGetraenk ? "Getränk bearbeiten" : "Neues Getränk erstellen"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="z.B. Mojito, Cola"
                    className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                    data-testid="getraenk-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Kategorie</Label>
                    <Select 
                      value={formData.kategorie} 
                      onValueChange={(value) => setFormData({...formData, kategorie: value})}
                    >
                      <SelectTrigger className="border-0 border-b border-stone-300 rounded-none px-0 focus:ring-0" data-testid="getraenk-kategorie-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {KATEGORIEN.map((kat) => (
                          <SelectItem key={kat} value={kat}>{kat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Einheit</Label>
                    <Select 
                      value={formData.einheit} 
                      onValueChange={(value) => {
                        setFormData({...formData, einheit: value});
                        setShowCustomEinheit(value === "Eigene");
                      }}
                    >
                      <SelectTrigger className="border-0 border-b border-stone-300 rounded-none px-0 focus:ring-0" data-testid="getraenk-einheit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EINHEITEN.map((einheit) => (
                          <SelectItem key={einheit} value={einheit}>{einheit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {showCustomEinheit && (
                      <Input
                        value={customEinheit}
                        onChange={(e) => setCustomEinheit(e.target.value)}
                        placeholder="z.B. 0.25l, Shot, Pitcher"
                        className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono mt-2"
                        data-testid="getraenk-custom-einheit-input"
                      />
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Lieferant</Label>
                  <Input
                    value={formData.lieferant}
                    onChange={(e) => setFormData({...formData, lieferant: e.target.value})}
                    placeholder="z.B. Großhändler XY"
                    className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                    data-testid="getraenk-lieferant-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Basis-Einkaufspreis (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.einkaufspreis}
                      onChange={(e) => setFormData({...formData, einkaufspreis: e.target.value})}
                      placeholder="0.00"
                      className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                      data-testid="getraenk-einkauf-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Verkaufspreis (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.verkaufspreis}
                      onChange={(e) => setFormData({...formData, verkaufspreis: e.target.value})}
                      placeholder="0.00"
                      className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                      data-testid="getraenk-verkauf-input"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column - Zutaten */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Zutaten hinzufügen (Eis, Limette, etc.)</Label>
                  <div className="flex gap-2">
                    <Select value={selectedZutat} onValueChange={handleZutatSelect}>
                      <SelectTrigger className="flex-1 border-stone-300 rounded-sm" data-testid="getraenk-zutat-select">
                        <SelectValue placeholder="Zutat wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__new__" className="text-[#3A4D39] font-medium">
                          <div className="flex items-center gap-2">
                            <PlusCircle size={14} />
                            Neue Zutat erstellen
                          </div>
                        </SelectItem>
                        <div className="border-t border-stone-200 my-1" />
                        {zutaten.map((z) => (
                          <SelectItem key={z.id} value={z.id}>
                            {z.name} ({formatCurrency(z.preis_pro_einheit)}/{z.einheit})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Menge und Einheit mit Umrechnung */}
                  {selectedZutat && (
                    <div className="flex gap-2 items-end mt-2">
                      <div className="flex-1">
                        <Label className="text-[10px] uppercase text-[#858585]">Menge</Label>
                        <Input
                          type="number"
                          step="0.001"
                          value={zutatMenge}
                          onChange={(e) => setZutatMenge(e.target.value)}
                          placeholder="0.00"
                          className="border-stone-300 rounded-sm font-mono"
                          data-testid="getraenk-zutat-menge-input"
                        />
                      </div>
                      <div className="w-20">
                        <Label className="text-[10px] uppercase text-[#858585]">Einheit</Label>
                        <Select value={zutatEinheit} onValueChange={setZutatEinheit}>
                          <SelectTrigger className="border-stone-300 rounded-sm" data-testid="getraenk-zutat-einheit-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(() => {
                              const zutat = zutaten.find(z => z.id === selectedZutat);
                              if (!zutat) return null;
                              const baseUnit = zutat.einheit;
                              const conversion = UNIT_CONVERSIONS[baseUnit];
                              const units = [baseUnit];
                              if (conversion) units.push(conversion.to);
                              return units.map(u => (
                                <SelectItem key={u} value={u}>{u}</SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                      </div>
                      {UNIT_CONVERSIONS[zutatEinheit] && (
                        <Button 
                          type="button"
                          variant="outline"
                          onClick={handleUnitConversion}
                          className="rounded-sm px-2"
                          title={UNIT_CONVERSIONS[zutatEinheit]?.label}
                          data-testid="convert-unit-btn"
                        >
                          <ArrowRightLeft size={16} />
                        </Button>
                      )}
                      <Button 
                        type="button"
                        onClick={addZutatToGetraenk}
                        className="bg-[#3A4D39] hover:bg-[#2C3B2B] rounded-sm"
                        data-testid="add-getraenk-zutat-btn"
                      >
                        <Plus size={16} />
                      </Button>
                    </div>
                  )}
                  
                  {/* Hinweis zur Umrechnung */}
                  {selectedZutat && UNIT_CONVERSIONS[zutatEinheit] && (
                    <p className="text-[10px] text-[#858585] mt-1">
                      Tipp: Klicken Sie ↔ um zwischen {zutatEinheit} und {UNIT_CONVERSIONS[zutatEinheit]?.to} umzurechnen
                    </p>
                  )}
                </div>

                {/* Zutaten List */}
                <div className="border border-stone-200 divide-y divide-stone-100 max-h-40 overflow-y-auto">
                  {formData.zutaten.length === 0 ? (
                    <div className="p-4 text-center text-[#858585] text-sm">
                      Keine Zutaten (optional für Softdrinks)
                    </div>
                  ) : (
                    formData.zutaten.map((z, idx) => {
                      // Finde Original-Zutat für Preis-Neuberechnung
                      const originalZutat = zutaten.find(oz => oz.id === z.zutat_id);
                      
                      const updateZutatMenge = (newMenge) => {
                        const menge = parseFloat(newMenge) || 0;
                        const kosten = originalZutat 
                          ? calculateZutatKosten(originalZutat, menge, z.einheit)
                          : z.kosten;
                        
                        const updatedZutaten = [...formData.zutaten];
                        updatedZutaten[idx] = {
                          ...z,
                          menge: menge,
                          kosten: Math.round(kosten * 100) / 100
                        };
                        setFormData({ ...formData, zutaten: updatedZutaten });
                      };
                      
                      return (
                        <div key={z.zutat_id || `zutat-${idx}`} className="flex items-center justify-between p-2 text-sm" data-testid={`getraenk-zutat-item-${idx}`}>
                          <div className="flex items-center gap-2 flex-1">
                            <span className="font-medium min-w-[100px]">{z.zutat_name}</span>
                            <Input
                              type="number"
                              step="0.001"
                              value={z.menge}
                              onChange={(e) => updateZutatMenge(e.target.value)}
                              className="w-20 h-7 text-xs font-mono border-stone-300 px-2"
                              data-testid={`edit-getraenk-menge-${idx}`}
                            />
                            <span className="text-[#858585] text-xs">{z.einheit}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-green-600">{formatCurrency(z.kosten)}</span>
                            <button
                              type="button"
                              onClick={() => removeZutatFromGetraenk(idx)}
                              className="text-[#C62828] hover:bg-red-50 p-1"
                              data-testid={`remove-getraenk-zutat-${idx}`}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Receipt Summary */}
                <div className="receipt-summary">
                  <div className="receipt-header">
                    <p className="receipt-title">Kalkulation</p>
                    <span className="text-xs text-slate-400">{land === "AT" ? "USt" : "MwSt"}: {vat.getraenke_label}</span>
                  </div>
                  <div className="receipt-line">
                    <span>Basis-Einkaufspreis:</span>
                    <span>{formatCurrency(parseFloat(formData.einkaufspreis) || 0)}</span>
                  </div>
                  {formData.zutaten.length > 0 && (
                    <div className="receipt-line text-[#858585]">
                      <span>+ Zutaten:</span>
                      <span>{formatCurrency(calculateZutatenKosten())}</span>
                    </div>
                  )}
                  <div className="receipt-line font-medium">
                    <span>= Gesamt-Einkauf:</span>
                    <span>{formatCurrency(calculateGesamtEinkauf())}</span>
                  </div>
                  <div className="receipt-line">
                    <span>VK (brutto):</span>
                    <span>{formatCurrency(parseFloat(formData.verkaufspreis) || 0)}</span>
                  </div>
                  <div className="receipt-line text-xs text-slate-400">
                    <span>VK (netto):</span>
                    <span>{formatCurrency(calculateNettoPreis())}</span>
                  </div>
                  <div className="receipt-line text-xs text-slate-400">
                    <span>{land === "AT" ? "USt" : "MwSt"} ({MWST_GETRAENKE}%):</span>
                    <span>{formatCurrency(calculateMwStBetrag())}</span>
                  </div>
                  <div className="receipt-divider" />
                  <div className="receipt-total">
                    <span>Marge (netto):</span>
                    <span className={calculateMargeNetto() >= 0 ? 'text-profit' : 'text-loss'}>
                      {formatCurrency(calculateMargeNetto())} ({calculateMargePercent()}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="rounded-sm"
                data-testid="cancel-getraenk-btn"
              >
                Abbrechen
              </Button>
              <Button 
                type="submit"
                className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm btn-press"
                data-testid="save-getraenk-btn"
              >
                <Check size={16} className="mr-2" />
                {editingGetraenk ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Neue Zutat Dialog */}
      <Dialog open={neueZutatDialogOpen} onOpenChange={setNeueZutatDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Neue Zutat erstellen</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleNeueZutatSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Name *</Label>
              <Input
                value={neueZutatForm.name}
                onChange={(e) => setNeueZutatForm({...neueZutatForm, name: e.target.value})}
                placeholder="z.B. Limette"
                className="border-stone-300 rounded-sm"
                data-testid="neue-zutat-getraenk-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Einheit</Label>
                <Select value={neueZutatForm.einheit} onValueChange={(v) => setNeueZutatForm({...neueZutatForm, einheit: v})}>
                  <SelectTrigger className="border-stone-300 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ZUTAT_EINHEITEN.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Preis pro {neueZutatForm.einheit} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={neueZutatForm.preis_pro_einheit}
                  onChange={(e) => setNeueZutatForm({...neueZutatForm, preis_pro_einheit: e.target.value})}
                  placeholder="0.00"
                  className="border-stone-300 rounded-sm font-mono"
                  data-testid="neue-zutat-getraenk-preis"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Lieferant (optional)</Label>
              <Input
                value={neueZutatForm.lieferant}
                onChange={(e) => setNeueZutatForm({...neueZutatForm, lieferant: e.target.value})}
                placeholder="z.B. Metro"
                className="border-stone-300 rounded-sm"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
              <Button type="button" variant="outline" onClick={() => setNeueZutatDialogOpen(false)} className="rounded-sm">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm">
                <Check size={16} className="mr-2" />
                Zutat erstellen
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Getraenke;
